// This file runs before React initialization to set up third-party scripts and environment-specific configurations

import { initSentry } from './tools/sentry/sentry';

interface GTMDataLayerItem {
  'gtm.start'?: number;
  event?: string;
  [key: string]: unknown;
}

initSentry();

// Usercentrics CMP integration
if (import.meta.env.VITE_ENV !== 'localhost') {
  const loadUsercentrics = () => {
    if (document.getElementById('usercentrics-cmp')) return;
    const usercentricsScript = document.createElement('script');
    usercentricsScript.setAttribute('id', 'usercentrics-cmp');
    usercentricsScript.setAttribute('src', 'https://web.cmp.usercentrics.eu/ui/loader.js');
    usercentricsScript.setAttribute('data-settings-id', 'UNQdQTMneRLW32');
    usercentricsScript.setAttribute('async', 'true');
    document.head.appendChild(usercentricsScript);
  };
  // Defer CMP well past the initial paint/LCP window so it doesn't compete
  // with the first mobile load.
  window.setTimeout(loadUsercentrics, 10000);
}

// Google Tag Manager integration
window.dataLayer = window.dataLayer || [];
(window.dataLayer as GTMDataLayerItem[]).push({
  'gtm.start': new Date().getTime(),
  event: 'gtm.js',
});

const gtmDynamicQueryParams = import.meta.env.VITE_GTM_PARAMS || '';
const gtmScript = document.createElement('script');
gtmScript.setAttribute('async', 'true');
gtmScript.setAttribute(
  'src',
  'https://www.googletagmanager.com/gtm.js?id=GTM-5QG3JBT7' +
    '&l=dataLayer' +
    `${gtmDynamicQueryParams}` +
    '&gtm_cookies_win=x',
);

// Load helper: run callback after Largest Contentful Paint (or fallback)
const runAfterLCP = (cb: () => void, timeout = 3000) => {
  if (typeof window === 'undefined') {
    setTimeout(cb, 0);
    return;
  }

  try {
    // Use PerformanceObserver to detect LCP (buffered) and run callback afterwards
    // If not supported, fall back to waiting for `load` event.
    if (typeof PerformanceObserver !== 'undefined') {
      const po = new PerformanceObserver((list) => {
        try {
          list.getEntries().forEach(() => {
            try {
              po.disconnect();
            } catch (err) {
              void err;
            }
            setTimeout(cb, 0);
          });
        } catch (outerErr) {
          try {
            po.disconnect();
          } catch (innerErr) {
            void innerErr;
          }
          void outerErr;
          setTimeout(cb, 0);
        }
      });

      po.observe({ type: 'largest-contentful-paint', buffered: true });

      // Ensure callback runs even if LCP doesn't fire in time
      setTimeout(() => {
        try {
          po.disconnect();
        } catch (err) {
          void err;
        }
        cb();
      }, timeout);
      return;
    }
  } catch (err) {
    void err;
  }

  window.addEventListener('load', () => setTimeout(cb, 1000), { once: true });
};

// Defer GTM loader until after LCP to avoid blocking LCP-critical work.
const loadGtm = () => {
  if (document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) return;
  document.head.appendChild(gtmScript);
};

runAfterLCP(loadGtm, 10000);

// Update document title based on environment
let titleText = 'sonnenHome Energy | sonnen';
if (import.meta.env.VITE_ENV !== 'production') {
  titleText = `${titleText} (${import.meta.env.VITE_ENV})`;
}

document.title = titleText;
